/**
 * Tenant Service
 *
 * Comprehensive API service for tenant management, plans, billing, locale settings,
 * pricing, quotas, and invoices.
 */

import { apiClient } from '@/lib/api-client';
import type {
  Plan,
  Tenant,
  TenantSettings,
  TenantUsage,
  BillingInfo,
  Invoice,
  BillingCycle,
  TenantPlan,
} from '../types';

const TENANT_BASE = '/tenant';
const PLANS_BASE = '/plans';

type BackendPlanName = 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';

interface BackendPlanConfig {
  name: BackendPlanName;
  displayName?: string;
  description?: string;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
  isPopular: boolean;
  sortOrder: number;
  maxEmployees: number;
  maxStorageBytes: number;
  maxAdmins: number;
  maxDepartments: number;
  apiRateLimitPerHour: number;
  auditLogRetentionDays: number;
  maxFileUploadBytes: number;
  features: Partial<Record<string, boolean>>;
}

interface BackendQuotaCheck {
  current: number;
  limit: number;
}

interface BackendQuotaStatus {
  plan: BackendPlanName;
  employees: BackendQuotaCheck;
  storage: BackendQuotaCheck;
  admins: BackendQuotaCheck;
  departments: BackendQuotaCheck;
  apiCalls: BackendQuotaCheck;
}

interface BackendCurrentPlanResponse {
  plan: BackendPlanConfig;
  quotaStatus: BackendQuotaStatus;
}

interface PresignedLogoUploadResponse {
  uploadUrl: string;
  fileKey: string;
}

interface LogoConfirmResponse {
  logoUrl: string;
}

function mapBackendPlan(plan: BackendPlanConfig): Plan {
  const mappedPlan: Plan = {
    id: plan.name.toLowerCase(),
    code: plan.name,
    name: plan.displayName ?? plan.name,
    monthlyPriceCents: Math.round(plan.monthlyPriceUsd * 100),
    annualPriceCents: Math.round(plan.annualPriceUsd * 100),
    maxEmployees: plan.maxEmployees,
    maxStorageBytes: plan.maxStorageBytes,
    maxAdmins: plan.maxAdmins,
    maxDepartments: plan.maxDepartments,
    apiRateLimitPerHour: plan.apiRateLimitPerHour,
    auditLogRetentionDays: plan.auditLogRetentionDays,
    maxFileUploadBytes: plan.maxFileUploadBytes,
    features: {
      employeeManagement: true,
      departmentManagement: true,
      attendanceTracking: Boolean(
        plan.features.basicAttendance || plan.features.advancedAttendance,
      ),
      leaveManagement: Boolean(plan.features.basicLeave || plan.features.advancedLeave),
      payrollIntegration: Boolean(plan.features.basicPayroll || plan.features.advancedPayroll),
      performanceReviews: false,
      recruitmentPipeline: false,
      trainingManagement: false,
      assetManagement: false,
      expenseManagement: false,
      crmModule: false,
      crmPipelines: false,
      crmAutomation: false,
      crmEmailTemplates: false,
      customBranding: Boolean(plan.features.whiteLabeling),
      ssoIntegration: Boolean(plan.features.sso),
      apiAccess: Boolean(plan.features.api),
      webhooks: Boolean(plan.features.webhooks),
      auditLogs: Boolean(plan.features.auditLogs),
      dataExport: Boolean(plan.features.dataExport),
      prioritySupport: Boolean(plan.features.prioritySupport),
      dedicatedManager: Boolean(plan.features.dedicatedManager),
      customIntegrations: Boolean(plan.features.webhooks || plan.features.api),
      slaGuarantee: Boolean(plan.features.dedicatedManager),
    },
    isActive: true,
    isDefault: plan.name === 'FREE',
    isPublic: true,
    isPopular: plan.isPopular,
    sortOrder: plan.sortOrder,
    requiresApproval: plan.name === 'ENTERPRISE',
    requiresSalesContact: plan.name === 'ENTERPRISE',
    trialDays: plan.name === 'FREE' ? 0 : 14,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  };

  if (plan.description !== undefined) {
    mappedPlan.description = plan.description;
  }

  return mappedPlan;
}

function mapQuotaStatusToUsage(quotaStatus: BackendQuotaStatus): TenantUsage {
  return {
    employees: quotaStatus.employees,
    storage: quotaStatus.storage,
    admins: quotaStatus.admins,
    departments: quotaStatus.departments,
    apiCalls: {
      current: quotaStatus.apiCalls.current,
      limit: quotaStatus.apiCalls.limit,
      resetAt: new Date(Date.now() + 60 * 60 * 1000),
    },
  };
}

function quotaPercentage(quota: BackendQuotaCheck): number {
  return quota.limit === -1 ? 0 : Math.round((quota.current / quota.limit) * 100);
}

function buildBillingPlaceholder(): BillingInfo & { isConfigured: false; provider: 'manual' } {
  return {
    cycle: 'monthly',
    isConfigured: false,
    provider: 'manual',
  };
}

// =============================================================================
// PLANS API
// =============================================================================

export const plansApi = {
  /**
   * Get all available plans
   */
  getAll: async (): Promise<Plan[]> => {
    const response = await apiClient.get<BackendPlanConfig[]>(PLANS_BASE);
    return response.map(mapBackendPlan);
  },

  /**
   * Get publicly available plans (for pricing page)
   */
  getPublic: async (): Promise<Plan[]> => {
    return plansApi.getAll();
  },

  /**
   * Get plan by ID
   */
  getById: async (id: string): Promise<Plan> => {
    const response = await apiClient.get<BackendPlanConfig>(`${PLANS_BASE}/${id}`);
    return mapBackendPlan(response);
  },

  /**
   * Get plan by code (FREE, STARTER, PROFESSIONAL, ENTERPRISE)
   */
  getByCode: async (code: TenantPlan): Promise<Plan> => {
    const response = await apiClient.get<BackendPlanConfig>(`${PLANS_BASE}/${code}`);
    return mapBackendPlan(response);
  },

  /**
   * Compare multiple plans side-by-side
   */
  compare: async (planCodes: TenantPlan[]): Promise<Plan[]> => {
    return Promise.all(planCodes.map((code) => plansApi.getByCode(code)));
  },

  /**
   * Get current tenant's active plan
   */
  getCurrent: async (): Promise<Plan> => {
    const response = await apiClient.get<BackendCurrentPlanResponse>(`${PLANS_BASE}/current`);
    return mapBackendPlan(response.plan);
  },
};

// =============================================================================
// PRICING API
// =============================================================================

export const pricingApi = {
  /**
   * Get pricing for all plans in a specific currency
   */
  getForCurrency: async (
    currency: string,
  ): Promise<{
    currency: string;
    plans: Array<{
      code: TenantPlan;
      monthlyPrice: number;
      annualPrice: number;
      annualDiscount: number;
    }>;
  }> => {
    const response = await apiClient.get<{
      currency: string;
      plans: Array<{
        code: TenantPlan;
        monthlyPrice: number;
        annualPrice: number;
        annualDiscount: number;
      }>;
    }>(`${PLANS_BASE}/pricing/${currency}`);
    return response;
  },

  /**
   * Get pricing tiers for a specific plan
   */
  getPlanTiers: async (
    planCode: TenantPlan,
  ): Promise<{
    monthly: number;
    annual: number;
    savings: number;
    savingsPercentage: number;
  }> => {
    const plan = await plansApi.getByCode(planCode);
    const annualMonthlyEquivalent = Math.round(plan.annualPriceCents / 12);
    const savings = Math.max(plan.monthlyPriceCents - annualMonthlyEquivalent, 0);
    return {
      monthly: plan.monthlyPriceCents,
      annual: plan.annualPriceCents,
      savings,
      savingsPercentage:
        plan.monthlyPriceCents > 0 ? Math.round((savings / plan.monthlyPriceCents) * 100) : 0,
    };
  },

  /**
   * Get supported currencies
   */
  getSupportedCurrencies: async (): Promise<
    Array<{
      code: string;
      name: string;
      symbol: string;
    }>
  > => {
    const response = await apiClient.get<
      Array<{
        code: string;
        name: string;
        symbol: string;
      }>
    >(`${PLANS_BASE}/locale/currencies`);
    return response;
  },
};

// =============================================================================
// TENANT API
// =============================================================================

export const tenantApi = {
  /**
   * Get current tenant information
   */
  getCurrent: async (): Promise<Tenant> => {
    const response = await apiClient.get<Tenant>(`${TENANT_BASE}/current`);
    return response;
  },

  /**
   * Get tenant settings
   */
  getSettings: async (): Promise<TenantSettings> => {
    const response = await apiClient.get<TenantSettings>(`${TENANT_BASE}/current/settings`);
    return response;
  },

  /**
   * Update tenant settings (CRUD)
   */
  updateSettings: async (settings: Partial<TenantSettings>): Promise<TenantSettings> => {
    const response = await apiClient.patch<TenantSettings>(
      `${TENANT_BASE}/current/settings`,
      settings,
    );
    return response;
  },

  /**
   * Reset tenant settings to defaults
   */
  resetSettings: async (): Promise<TenantSettings> => {
    const response = await apiClient.post<TenantSettings>(`${TENANT_BASE}/current/settings/reset`);
    return response;
  },

  /**
   * Get tenant usage/quotas
   */
  getUsage: async (): Promise<TenantUsage> => {
    const response = await apiClient.get<BackendCurrentPlanResponse>(`${PLANS_BASE}/current`);
    return mapQuotaStatusToUsage(response.quotaStatus);
  },

  /**
   * Get detailed quota information
   */
  getQuotas: async (): Promise<{
    employees: { current: number; limit: number; percentage: number };
    storage: { current: number; limit: number; percentage: number };
    admins: { current: number; limit: number; percentage: number };
    departments: { current: number; limit: number; percentage: number };
    apiCalls: { current: number; limit: number; resetAt: Date };
  }> => {
    const usage = await tenantApi.getUsage();
    return {
      employees: { ...usage.employees, percentage: quotaPercentage(usage.employees) },
      storage: { ...usage.storage, percentage: quotaPercentage(usage.storage) },
      admins: { ...usage.admins, percentage: quotaPercentage(usage.admins) },
      departments: { ...usage.departments, percentage: quotaPercentage(usage.departments) },
      apiCalls: usage.apiCalls,
    };
  },

  /**
   * Upload company logo (presigned URL pattern)
   * Step 1: Get presigned URL → Step 2: PUT file to S3 → Step 3: Confirm
   */
  uploadLogo: async (file: File): Promise<{ logoUrl: string }> => {
    // Step 1: Get presigned upload URL
    const presign = await apiClient.post<PresignedLogoUploadResponse>(
      `${TENANT_BASE}/current/logo/presign`,
      {
        fileName: file.name,
        contentType: file.type || 'image/png',
        fileSize: file.size,
      },
    );

    // Step 2: Upload directly to S3/MinIO using the presigned URL
    await fetch(presign.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/png' },
      body: file,
    });

    // Step 3: Confirm the upload with the backend
    const confirmed = await apiClient.post<LogoConfirmResponse>(
      `${TENANT_BASE}/current/logo/confirm`,
      {
        fileKey: presign.fileKey,
      },
    );

    return confirmed;
  },

  /**
   * Remove company logo
   */
  removeLogo: async (): Promise<void> => {
    await apiClient.delete(`${TENANT_BASE}/current/logo`);
  },

  /**
   * Get tenant statistics
   */
  getStatistics: async (): Promise<{
    employeeCount: number;
    departmentCount: number;
    storageUsed: number;
    apiCallsToday: number;
    lastActivity: Date;
  }> => {
    const response = await apiClient.get<{
      employeeCount: number;
      departmentCount: number;
      storageUsed: number;
      apiCallsToday: number;
      lastActivity: Date;
    }>(`${TENANT_BASE}/current/statistics`);
    return response;
  },
};

// =============================================================================
// LOCALE SETTINGS API
// =============================================================================

export const localeApi = {
  /**
   * Get current locale settings (timezone, currency, language)
   */
  getSettings: async (): Promise<{
    timezone: string;
    currency: string;
    locale: string;
    dateFormat: string;
    timeFormat: '12h' | '24h';
    weekStartsOn: 0 | 1 | 6;
  }> => {
    const response = await apiClient.get<{
      timezone: string;
      currency: string;
      locale: string;
      dateFormat: string;
      timeFormat: '12h' | '24h';
      weekStartsOn: 0 | 1 | 6;
    }>(`${TENANT_BASE}/current/settings`);
    return response;
  },

  /**
   * Update timezone
   */
  updateTimezone: async (timezone: string): Promise<{ timezone: string }> => {
    const response = await apiClient.patch<{ timezone: string }>(
      `${TENANT_BASE}/current/settings`,
      {
        timezone,
      },
    );
    return response;
  },

  /**
   * Update currency
   */
  updateCurrency: async (currency: string): Promise<{ currency: string }> => {
    const response = await apiClient.patch<{ currency: string }>(
      `${TENANT_BASE}/current/settings`,
      {
        currency,
      },
    );
    return response;
  },

  /**
   * Update language/locale
   */
  updateLanguage: async (locale: string): Promise<{ locale: string }> => {
    const response = await apiClient.patch<{ locale: string }>(`${TENANT_BASE}/current/settings`, {
      language: locale,
    });
    return response;
  },

  /**
   * Get available timezones
   */
  getTimezones: async (): Promise<
    Array<{
      value: string;
      label: string;
      offset: string;
    }>
  > => {
    // Static list — no backend endpoint needed
    return [
      { value: 'Pacific/Honolulu', label: 'Hawaii (UTC-10:00)', offset: '-10:00' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-08:00)', offset: '-08:00' },
      { value: 'America/Denver', label: 'Mountain Time (UTC-07:00)', offset: '-07:00' },
      { value: 'America/Chicago', label: 'Central Time (UTC-06:00)', offset: '-06:00' },
      { value: 'America/New_York', label: 'Eastern Time (UTC-05:00)', offset: '-05:00' },
      { value: 'Europe/London', label: 'London (UTC+00:00)', offset: '+00:00' },
      { value: 'Europe/Paris', label: 'Paris, Berlin (UTC+01:00)', offset: '+01:00' },
      { value: 'Europe/Moscow', label: 'Moscow (UTC+03:00)', offset: '+03:00' },
      { value: 'Asia/Dubai', label: 'Dubai (UTC+04:00)', offset: '+04:00' },
      { value: 'Asia/Kolkata', label: 'Mumbai, New Delhi (UTC+05:30)', offset: '+05:30' },
      { value: 'Asia/Dhaka', label: 'Dhaka (UTC+06:00)', offset: '+06:00' },
      { value: 'Asia/Bangkok', label: 'Bangkok (UTC+07:00)', offset: '+07:00' },
      { value: 'Asia/Singapore', label: 'Singapore (UTC+08:00)', offset: '+08:00' },
      { value: 'Asia/Tokyo', label: 'Tokyo (UTC+09:00)', offset: '+09:00' },
      { value: 'Australia/Sydney', label: 'Sydney (UTC+10:00)', offset: '+10:00' },
      { value: 'Pacific/Auckland', label: 'Auckland (UTC+12:00)', offset: '+12:00' },
    ];
  },

  /**
   * Get available languages
   */
  getLanguages: async (): Promise<
    Array<{
      code: string;
      name: string;
      nativeName: string;
    }>
  > => {
    // Static list — no backend endpoint needed
    return [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'en-IN', name: 'English (India)', nativeName: 'English (India)' },
      { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
      { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
      { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
      { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
      { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
      { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
      { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
      { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
    ];
  },
};

// =============================================================================
// SUBSCRIPTION API
// =============================================================================

export const subscriptionApi = {
  /**
   * Get current subscription information
   */
  getCurrent: async (): Promise<{
    plan: Plan;
    billing: BillingInfo;
    usage: TenantUsage;
  }> => {
    const response = await apiClient.get<BackendCurrentPlanResponse>(`${PLANS_BASE}/current`);
    return {
      plan: mapBackendPlan(response.plan),
      billing: buildBillingPlaceholder(),
      usage: mapQuotaStatusToUsage(response.quotaStatus),
    };
  },

  /**
   * Upgrade to a higher plan
   */
  upgrade: async (data: {
    planCode: TenantPlan;
    cycle: BillingCycle;
  }): Promise<{ success: boolean; checkoutUrl?: string }> => {
    await apiClient.post(`${PLANS_BASE}/change`, {
      targetPlan: data.planCode,
      reason: `Upgrade requested from Plan & Usage (${data.cycle})`,
    });
    return { success: true };
  },

  /**
   * Downgrade to a lower plan
   */
  downgrade: async (data: {
    planCode: TenantPlan;
    reason?: string;
  }): Promise<{ success: boolean; effectiveDate: Date }> => {
    await apiClient.post(`${PLANS_BASE}/change`, {
      targetPlan: data.planCode,
      reason: data.reason ?? 'Downgrade requested from Plan & Usage',
    });
    return { success: true, effectiveDate: new Date() };
  },

  /**
   * Cancel subscription
   */
  cancel: async (data: {
    reason: string;
    feedback?: string;
  }): Promise<{ success: boolean; effectiveDate: Date }> => {
    void data;
    throw new Error('Subscription cancellation is not configured for this tenant yet.');
  },

  /**
   * Reactivate cancelled subscription
   */
  reactivate: async (): Promise<{ success: boolean }> => {
    throw new Error('Subscription reactivation is not configured for this tenant yet.');
  },

  /**
   * Change billing cycle (monthly <-> annual)
   */
  changeCycle: async (cycle: BillingCycle): Promise<{ success: boolean }> => {
    void cycle;
    throw new Error('Billing-cycle changes are not configured for this tenant yet.');
  },

  /**
   * Change plan (general purpose - handles both upgrades and downgrades)
   */
  changePlan: async (data: {
    planCode: TenantPlan;
    cycle?: BillingCycle;
    reason?: string;
  }): Promise<{
    success: boolean;
    checkoutUrl?: string;
    effectiveDate?: Date;
  }> => {
    await apiClient.post(`${PLANS_BASE}/change`, {
      targetPlan: data.planCode,
      reason: data.reason ?? `Plan change requested from Plan & Usage (${data.cycle ?? 'monthly'})`,
    });
    return { success: true, effectiveDate: new Date() };
  },

  /**
   * Preview plan change (see costs before committing)
   */
  previewChange: async (data: {
    planCode: TenantPlan;
    cycle?: BillingCycle;
  }): Promise<{
    currentPlan: TenantPlan;
    newPlan: TenantPlan;
    proratedAmount: number;
    nextBillingAmount: number;
    nextBillingDate: Date;
  }> => {
    const current = await plansApi.getCurrent();
    const target = await plansApi.getByCode(data.planCode);
    return {
      currentPlan: current.code,
      newPlan: target.code,
      proratedAmount: 0,
      nextBillingAmount:
        data.cycle === 'annual' ? target.annualPriceCents : target.monthlyPriceCents,
      nextBillingDate: new Date(),
    };
  },
};

// =============================================================================
// BILLING API
// =============================================================================

export const billingApi = {
  /**
   * Get billing information
   */
  getInfo: async (): Promise<BillingInfo> => {
    return buildBillingPlaceholder();
  },

  /**
   * Get billing history/invoices with pagination
   */
  getInvoices: async (params?: {
    page?: number;
    limit?: number;
    status?: 'paid' | 'pending' | 'overdue' | 'cancelled';
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: Invoice[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    return { data: [], total: 0, page, limit, totalPages: 0 };
  },

  /**
   * Get single invoice by ID
   */
  getInvoice: async (id: string): Promise<Invoice> => {
    void id;
    throw new Error('Invoices are not configured for this tenant yet.');
  },

  /**
   * Download invoice as PDF
   */
  downloadInvoice: async (id: string): Promise<Blob> => {
    void id;
    throw new Error('Invoice downloads are not configured for this tenant yet.');
  },

  /**
   * Send invoice via email
   */
  emailInvoice: async (id: string, email?: string): Promise<{ success: boolean }> => {
    void id;
    void email;
    throw new Error('Invoice email delivery is not configured for this tenant yet.');
  },

  /**
   * Update payment method (redirects to payment gateway)
   */
  updatePaymentMethod: async (): Promise<{ checkoutUrl: string }> => {
    throw new Error('Payment-method management is not configured for this tenant yet.');
  },

  /**
   * Get upcoming invoice preview
   */
  getUpcomingInvoice: async (): Promise<{
    amount: number;
    date: Date;
    items: Array<{ description: string; amount: number }>;
  }> => {
    return { amount: 0, date: new Date(), items: [] };
  },

  /**
   * Get billing history summary/stats
   */
  getHistory: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    totalSpent: number;
    averageMonthly: number;
    invoiceCount: number;
    paidCount: number;
    overdueCount: number;
    transactions: Array<{
      date: Date;
      amount: number;
      status: string;
      invoiceId: string;
    }>;
  }> => {
    void params;
    return {
      totalSpent: 0,
      averageMonthly: 0,
      invoiceCount: 0,
      paidCount: 0,
      overdueCount: 0,
      transactions: [],
    };
  },

  /**
   * Get payment methods on file
   */
  getPaymentMethods: async (): Promise<
    Array<{
      id: string;
      type: 'card' | 'bank';
      last4: string;
      brand?: string;
      expiryMonth?: number;
      expiryYear?: number;
      isDefault: boolean;
    }>
  > => {
    return [];
  },

  /**
   * Remove a payment method
   */
  removePaymentMethod: async (id: string): Promise<void> => {
    void id;
    throw new Error('Payment-method management is not configured for this tenant yet.');
  },

  /**
   * Set default payment method
   */
  setDefaultPaymentMethod: async (id: string): Promise<{ success: boolean }> => {
    void id;
    throw new Error('Payment-method management is not configured for this tenant yet.');
  },
};

// =============================================================================
// EXPORT
// =============================================================================

export const tenantService = {
  plans: plansApi,
  pricing: pricingApi,
  tenant: tenantApi,
  locale: localeApi,
  subscription: subscriptionApi,
  billing: billingApi,
};

export default tenantService;
